import { useCallback, useEffect, useState } from "react";
import { PolicySaysChip } from "../../components/PolicySaysChip";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, ChevronUp, ClipboardList, LifeBuoy, RefreshCw } from "lucide-react";
import { theme, card, btnPrimary, type as text, pageHeading, pageSubtitle } from "../../theme";
import { api, apiErrorMessage } from "../../api/client";
import { useCurrentUser } from "../../auth/useCurrentUser";
import { PortalLink } from "../../components/PortalLink";
import { LoadingBlock } from "../../components/Spinner";
import { fmtDay, relativeDue, secondaryBtn, type ResilienceSummary, type AttentionItem } from "./shared";
import { IncidentsTab } from "./IncidentsTab";
import { ExercisesTab } from "./ExercisesTab";
import { TrainingTab } from "./TrainingTab";
import { AssetsTab } from "./AssetsTab";
import { InsuranceTab } from "./InsuranceTab";
import { BiaTab } from "./BiaTab";
import { OverviewUpcoming } from "./OverviewUpcoming";
import { OverviewReadiness } from "./OverviewReadiness";
import { ReportIncidentButton } from "./ReportIncident";

export type ResilienceTab = "overview" | "incidents" | "exercises" | "training" | "assets" | "insurance" | "bia";

// ── Page ──────────────────────────────────────────────────────────────────────
// Operational resilience: the registers every standard expects (incidents,
// exercises, training, assets, insurance) and the notifications one incident
// derives from the organisation's laws. Base tier; writes need resilience:write, which
// controls:write implies (mirrored here as canEdit). Any member may report an incident
// (ReportIncident.tsx). Contract: docs/OPERATIONAL-RESILIENCE.md.

// The operating parameters each register runs to (Settings -> Operating parameters).
const TAB_PARAMETERS: Partial<Record<ResilienceTab, string[]>> = {
  incidents: ["incident.pir_days"],
  exercises: ["exercise.response_months", "exercise.recovery_months", "backup.restore_test_months"],
  training: ["training.refresh_months"],
  assets: ["asset.end_of_support_warning_days", "exercise.recovery_months"],
};

export function ResiliencePage({ tab }: { tab: ResilienceTab }) {
  const { user } = useCurrentUser();
  const canEdit = !!user?.permissions?.includes("resilience:write");
  const [summary, setSummary] = useState<ResilienceSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncNote, setSyncNote] = useState<string | null>(null);

  // Queued on the worker: say so, block a second click while it is being asked, and show a refusal (RES-26).
  async function syncEvidence() {
    if (syncing) return;
    setSyncing(true); setSyncNote(null);
    try { await api.post("/resilience/evidence/sync"); setSyncNote("Evidence refresh started. The register documents update in the Evidence Repository in a few minutes."); }
    catch (e) { setError(apiErrorMessage(e, "Could not start the evidence refresh")); }
    finally { setSyncing(false); }
  }

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await api.get<ResilienceSummary>("/resilience/summary"));
      setError(null);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load the resilience summary"));
    }
  }, []);

  useEffect(() => { void loadSummary(); }, [loadSummary]);

  return (
    <div className="page-pad">
      {/* No tab row here: the registers are the Incidents area's tabs in the shell
          (nav/clientNav.ts, frontend-jsy nav/jsyNav.ts); a second row only repeated them. */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}>
            <LifeBuoy size={20} /> Operational resilience
          </h1>
          <p style={{ ...pageSubtitle, maxWidth: 760 }}>
            Record incidents and the regulators they must reach, exercise the plans, evidence training and know which assets matter — the records your standards and the incident-reporting laws where you operate expect.
          </p>
          {TAB_PARAMETERS[tab] && <PolicySaysChip keys={TAB_PARAMETERS[tab]!} />}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {/* Editors raise incidents from the Incidents tab with the full form; everyone else reports here. */}
          {!canEdit && user?.role === "client" && <ReportIncidentButton onReported={() => void loadSummary()} />}
          {canEdit && (
            <button type="button" style={{ ...secondaryBtn, opacity: syncing ? 0.6 : 1 }} disabled={syncing} onClick={() => void syncEvidence()} title="Refresh the register documents in the Evidence Repository and re-check deadlines">
              <RefreshCw size={12} style={{ verticalAlign: -2, marginRight: 6 }} />{syncing ? "Syncing..." : "Sync evidence"}
            </button>
          )}
        </div>
      </div>

      {error && <div style={{ ...card, ...text.body, padding: 14, marginBottom: 16, color: theme.color.dangerText }}>{error}</div>}
      {syncNote && <div role="status" style={{ ...text.hint, marginBottom: 16 }}>{syncNote}</div>}

      {tab === "overview"  && <Overview summary={summary} canEdit={canEdit} />}
      {tab === "incidents" && <IncidentsTab canEdit={canEdit} context={summary?.context ?? null} playbook={summary?.playbook ?? []} onChanged={loadSummary} pendingNotifications={summary?.counts.pendingNotifications ?? 0} />}
      {tab === "exercises" && <ExercisesTab canEdit={canEdit} tprmEnabled={!!summary?.context.tprmEnabled} onChanged={loadSummary} />}
      {tab === "training"  && <TrainingTab canEdit={canEdit} onChanged={loadSummary} />}
      {tab === "assets"    && <AssetsTab canEdit={canEdit} tprmEnabled={!!summary?.context.tprmEnabled} onChanged={loadSummary} />}
      {tab === "insurance" && <InsuranceTab canEdit={canEdit} onChanged={loadSummary} />}
      {tab === "bia"       && <BiaTab canEdit={canEdit} onChanged={loadSummary} />}
    </div>
  );
}

// ── Overview ──────────────────────────────────────────────────────────────────

function Tile({ label, value, sub, to }: { label: string; value: string | number; sub?: string; to: string }) {
  return (
    <PortalLink to={to} style={{ textDecoration: "none" }}>
      <div style={{ ...card, padding: "14px 16px", height: "100%" }}>
        <div style={text.statLabel}>{label}</div>
        <div style={{ ...text.stat, marginTop: 4 }}>{value}</div>
        {sub && <div style={{ ...text.hint, marginTop: 2 }}>{sub}</div>}
      </div>
    </PortalLink>
  );
}

const SEV_COLOR: Record<AttentionItem["severity"], string> = { high: "var(--c-danger)", medium: "var(--c-warning)", low: "var(--c-borderStrong)" };
const SEV_PILL: Record<AttentionItem["severity"], { bg: string; color: string; label: string }> = {
  high:   { bg: theme.color.dangerBg,  color: theme.color.dangerText,  label: "Act now" },
  medium: { bg: theme.color.warningBg, color: theme.color.warningText, label: "Action" },
  low:    { bg: theme.color.panelAlt,  color: theme.color.muted,       label: "Recommended" },
};

/** The register an attention item belongs to, from where it links. */
function registerOf(href: string): string | null {
  const m = /^\/resilience\/(incidents|exercises|training|assets|insurance|bia)/.exec(href);
  return m ? m[1] : null;
}

/**
 * One line per register saying what it still lacks, from the summary counts and
 * the attention items that point into it. A register with nothing missing says so.
 */
function RegisterCompleteness({ summary }: { summary: ResilienceSummary }) {
  const c = summary.counts;
  const open = (reg: string) => summary.attention.filter((a) => a.severity !== "low" && registerOf(a.href) === reg).length;
  const insuranceDays = summary.insurance ? (new Date(summary.insurance.expiresAt).getTime() - Date.now()) / 86_400_000 : null;
  const rows: Array<{ label: string; to: string; missing: string | null; urgent?: boolean }> = [
    {
      label: "Cyber incidents", to: "/resilience/incidents",
      missing: c.pendingNotifications > 0 ? `${c.pendingNotifications} required notification${c.pendingNotifications === 1 ? "" : "s"} still to send`
        : open("incidents") ? `${open("incidents")} incident item${open("incidents") === 1 ? "" : "s"} need attention`
        : c.incidentsYear === 0 ? "Nothing recorded in 12 months — record near misses and phishing reports too" : null,
      urgent: c.pendingNotifications > 0,
    },
    {
      label: "Exercises & testing", to: "/resilience/exercises",
      missing: c.exercisesCompletedYear === 0 ? "No exercise completed in the last 12 months"
        : c.exercisesPlanned === 0 ? "Nothing planned for the year ahead"
        : open("exercises") ? `${open("exercises")} exercise item${open("exercises") === 1 ? "" : "s"} need attention` : null,
    },
    {
      label: "Training & awareness", to: "/resilience/training",
      missing: c.trainingRecords === 0 ? "No training recorded" : open("training") ? `${open("training")} training item${open("training") === 1 ? "" : "s"} need attention` : null,
    },
    {
      label: "Asset register", to: "/resilience/assets",
      missing: c.assets === 0 ? "No assets recorded — start with the business services"
        : c.criticalAssets === 0 ? "No asset marked critical or essential"
        : open("assets") ? `${open("assets")} asset item${open("assets") === 1 ? "" : "s"} need attention` : null,
    },
    {
      label: "Cyber insurance", to: "/resilience/insurance",
      missing: !summary.insurance ? "No policy recorded — or record the decision not to insure"
        : insuranceDays != null && insuranceDays < 0 ? `Expired ${fmtDay(summary.insurance.expiresAt)}`
        : insuranceDays != null && insuranceDays < 60 ? `Renews ${fmtDay(summary.insurance.expiresAt)}`
        : summary.insurance.notificationWindowHours == null ? "Insurer notification window not recorded" : null,
      urgent: insuranceDays != null && insuranceDays < 0,
    },
  ];
  return (
    <div style={{ ...card, padding: 0 }}>
      <div style={{ ...text.sectionTitle, margin: 0, padding: "14px 18px", borderBottom: "1px solid var(--c-border)", display: "flex", alignItems: "center", gap: 8 }}>
        <ClipboardList size={15} /> Register completeness
        <span style={{ ...text.hint, fontWeight: 400 }}>{rows.filter((r) => !r.missing).length} of {rows.length} complete</span>
      </div>
      {rows.map((r) => (
        <PortalLink key={r.to} to={r.to} style={{ textDecoration: "none" }}>
          <div style={{ display: "flex", gap: 10, padding: "10px 18px", borderBottom: "1px solid var(--c-border)", alignItems: "center", flexWrap: "wrap" }}>
            {r.missing
              ? <AlertTriangle size={14} style={{ color: r.urgent ? theme.color.dangerText : theme.color.warningText, flexShrink: 0 }} />
              : <CheckCircle2 size={14} style={{ color: theme.color.successText, flexShrink: 0 }} />}
            <span style={{ ...text.itemTitle, minWidth: 150 }}>{r.label}</span>
            <span style={{ ...text.hint, color: r.missing ? (r.urgent ? theme.color.dangerText : theme.color.muted) : theme.color.successText, flex: 1, minWidth: 180 }}>{r.missing ?? "Nothing missing"}</span>
            <ChevronRight size={14} style={{ color: theme.color.faint, flexShrink: 0 }} />
          </div>
        </PortalLink>
      ))}
    </div>
  );
}

function Overview({ summary, canEdit }: { summary: ResilienceSummary | null; canEdit: boolean }) {
  const [showPlaybook, setShowPlaybook] = useState(false);
  if (!summary) return <LoadingBlock />;
  const c = summary.counts;
  const ctx = summary.context;
  const actions = summary.attention.filter((a) => a.severity !== "low");
  const recommendations = summary.attention.filter((a) => a.severity === "low");

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
        <Tile label="Open incidents" value={c.incidentsOpen} sub={`${c.incidentsYear} in the last 12 months`} to="/resilience/incidents" />
        <Tile label="Notifications pending" value={c.pendingNotifications} sub="required, not yet sent" to="/resilience/incidents" />
        <Tile label="Exercises" value={c.exercisesCompletedYear} sub={`completed in 12 months · ${c.exercisesPlanned} planned`} to="/resilience/exercises" />
        <Tile label="Training records" value={c.trainingRecords} to="/resilience/training" />
        <Tile label="Assets" value={c.assets} sub={`${c.criticalAssets} critical / essential`} to="/resilience/assets" />
        <Tile label="Cyber insurance" value={summary.insurance ? summary.insurance.insurer : "None"} sub={summary.insurance ? `expires ${fmtDay(summary.insurance.expiresAt)}` : "no policy recorded"} to={summary.insurance ? `/resilience/insurance?policy=${summary.insurance.id}` : "/resilience/insurance"} />
      </div>

      <OverviewUpcoming />
      <OverviewReadiness />

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(280px, 1fr)", gap: 20 }}>
        <div style={{ ...card, padding: 0 }}>
          <div style={{ ...text.sectionTitle, margin: 0, padding: "14px 18px", borderBottom: "1px solid var(--c-border)", display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={15} /> Needs attention
            <span style={{ ...text.hint, fontWeight: 400 }}>{actions.length} action{actions.length === 1 ? "" : "s"} · {recommendations.length} recommendation{recommendations.length === 1 ? "" : "s"}</span>
          </div>
          {summary.attention.length === 0 ? (
            <div style={{ ...text.body, padding: 20, color: theme.color.muted }}>Nothing outstanding.</div>
          ) : (
            <div>
              {[...actions, ...recommendations].map((a) => (
                <PortalLink key={a.id} to={a.href} style={{ textDecoration: "none" }}>
                  <div style={{ display: "flex", gap: 12, padding: "11px 18px", borderBottom: "1px solid var(--c-border)", alignItems: "flex-start", borderLeft: `3px solid ${SEV_COLOR[a.severity]}` }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 2, background: SEV_PILL[a.severity].bg, color: SEV_PILL[a.severity].color, whiteSpace: "nowrap" }}>{SEV_PILL[a.severity].label}</span>
                        <span style={text.itemTitle}>{a.title}</span>
                      </div>
                      <div style={{ ...text.hint, marginTop: 4 }}>{a.description}</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                      {a.dueAt && <span style={{ ...text.meta, fontWeight: 600, color: new Date(a.dueAt).getTime() < Date.now() || a.severity === "high" ? theme.color.dangerText : theme.color.muted, whiteSpace: "nowrap" }}>{relativeDue(a.dueAt)}</span>}
                      <span style={{ fontSize: text.hint.fontSize, fontWeight: 600, color: "var(--c-accentText)", display: "inline-flex", alignItems: "center", gap: 2, whiteSpace: "nowrap" }}>{canEdit ? "Deal with it" : "Open"}<ChevronRight size={13} /></span>
                    </div>
                  </div>
                </PortalLink>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "grid", gap: 16, alignContent: "start" }}>
          <RegisterCompleteness summary={summary} />
          <div style={{ ...card, padding: 18 }}>
            <div style={text.sectionTitle}>Who an incident must reach</div>

            <div style={text.hint}>
              Derived from Operating Locations ({ctx.operatingCountries.join(", ") || "none set"}), the laws in scope ({ctx.regulations.length}) and the threshold profile.
            </div>
            <ul style={{ ...text.hint, color: theme.color.text, margin: "10px 0 0", paddingLeft: 18, lineHeight: 1.6 }}>
              {ctx.oesRegimes.length === 0 && <li>No operator-of-essential-services regime applies{ctx.regulations.some((r) => /Cyber Security \(Jersey\)|NIS/.test(r)) ? " — answer “Designated operator of essential services” on the Legal obligations profile if that is wrong" : ""}.</li>}
              {ctx.oesRegimes.map((r) => <li key={r.regulation}><strong>{r.regulation}</strong>: {r.status === "required" ? "in scope — statutory incident notification applies" : "possible duty — confirm your designation"}</li>)}
              {ctx.doraMonitoringEnabled && <li>DORA register in use — major ICT incidents run the Art. 19 clocks there.</li>}
              <li>Data protection authorities for every law in scope when personal data is involved.</li>
              <li>{summary.insurance ? `Insurer ${summary.insurance.insurer}${summary.insurance.notificationWindowHours ? ` within ${summary.insurance.notificationWindowHours} h` : ""}.` : "No insurer recorded."}</li>
              <li>National CERT{ctx.certSources.length ? ` (${ctx.certSources.join(", ")})` : ""} and law enforcement as voluntary routes.</li>
            </ul>
            <div style={{ marginTop: 10 }}>
              <PortalLink to="/legal" style={{ fontSize: text.hint.fontSize, color: "var(--c-accentText)" }}>Threshold profile and legal obligations</PortalLink>
            </div>
          </div>

          <div style={{ ...card, padding: 18 }}>
            <button type="button" onClick={() => setShowPlaybook((v) => !v)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", background: "none", border: "none", padding: 0, cursor: "pointer", ...text.sectionTitle, margin: 0 }}>
              Ransomware and cyber-extortion playbook {showPlaybook ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            <div style={{ ...text.hint, marginTop: 4 }}>The decision path, with the sanctions authorities for your jurisdictions. Generate the full document on the Write policies page.</div>
            {showPlaybook && (
              <ol style={{ ...text.hint, color: theme.color.text, margin: "12px 0 0", paddingLeft: 18, display: "grid", gap: 8 }}>
                {summary.playbook.map((s) => (
                  <li key={s.key}><strong>{s.title}.</strong> {s.detail} <span style={{ color: theme.color.faint }}>({s.basis})</span></li>
                ))}
              </ol>
            )}
            <div style={{ marginTop: 10 }}>
              <PortalLink to="/templates" style={{ fontSize: text.hint.fontSize, color: "var(--c-accentText)" }}>Generate the playbook document</PortalLink>
            </div>
          </div>
        </div>
      </div>

      {canEdit && c.incidentsOpen === 0 && c.incidentsYear === 0 && (
        <div style={{ ...card, padding: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ ...text.body, color: theme.color.muted }}>No incidents recorded yet. Record even minor events — near misses and phishing reports build the picture regulators and insurers ask for.</div>
          <PortalLink to="/resilience/incidents" style={{ ...btnPrimary, textDecoration: "none" }}>Open the incident register</PortalLink>
        </div>
      )}
      <div style={text.meta}>Notification rules last reviewed {fmtDay(summary.lastReviewed)}.</div>
    </div>
  );
}
